<template>
    <div class="h-full min-h-[70px] overflow-hidden rounded-xl border border-solid border-grey-40 px-2  py-3"
         :class="{'message-render-disclosure-copilot': createStepList?.length}"
    >
        <div class="h-full overflow-auto">
            <TimeLine ref="container" :steps="steps" :currentStep="currentStep">
                <template #1>
                    <div v-if="currentStep >= 1" class="flex flex-col gap-2">
                        {{displayQuestion}}
                    </div>
                    <div v-else>{{ t('chat.stepExplore.tip') }}</div>
                </template>
                <template v-if="currentStep >= 2" #2>
                    <div v-if="!isAnalyzing" class="flex flex-col gap-2">
                        <div :key="item.key" v-for="item in directory" class="th-tooltip" :data-tip="t('chat.stepOutline.systemTip')">
                            <div class="flex items-center gap-3 rounded bg-grey-10 px-3 py-2 text-left hover:bg-blue-40">
                                {{ item.name }}
                            </div>
                        </div>
                        <div :key="index" v-for="(directoryItem, index) in customizeDirectory" class="group flex items-center gap-2">
                            <input ref="customizeInputRef" :disabled="isProjectFinished" v-model="directoryItem.name" type="text" :placeholder="t('chat.stepOutline.placeholder')" class="th-input w-full" maxlength="256" />
                            <button v-if="!isProjectFinished" @click="handleDirectoryRemove(index)" class="th-btn-only-icon th-btn th-btn-plain invisible shrink-0" :class="{'group-hover:visible': !isProjectFinished}">
                                <PtIcon :icon="iconTrash" class="th-btn-icon text-xl" />
                            </button>
                        </div>
                        <div v-show="showOutlineAdd">
                            <button @click="handleAddClick" class="th-btn th-btn-text">
                                <PtIcon :icon="iconAdd" class="th-btn-icon text-xl" />
                                {{ t('chat.stepOutline.add') }}
                            </button>
                        </div>

                        <div v-show="!isProjectFinished" class="border-t border-grey-40 pt-4">
                            <button :disabled="exploreDisabled" @click="handleCreateClick" class="th-btn">
                                <PtIcon :icon="iconCopilotWhite" class="th-btn-icon text-[24px]"></PtIcon>
                                <span>{{ t('chat.stepOutline.btn') }}</span>
                            </button>
                        </div>
                    </div>
                    <span v-else class="th-loading th-loading-bars th-loading-lg mx-auto my-2 block text-grey-450"></span>
                </template>
                <template v-if="currentStep >= 3" #3>
                    <template v-if="createStepList?.length">
                        <CreateStepRow
                            v-for="(item, index) in createStepList"
                            :key="index"
                            :progressItem="item">
                        </CreateStepRow>
                        <div v-show="!isProjectFinished" class="flex h-10 items-center gap-2 px-2" v-if="createStepList?.length">
                            <PtIcon :icon="iconCopilot" class="text-[40px]" :class="{'jump': isStreamProcessing}"></PtIcon>
                            <span class="text-sm font-semibold text-eureka-500">
                                {{ titleLabel }}
                                <span v-show="countdown > -1">({{ countdown }}s)</span>
                            </span>
                        </div>
                    </template>
                    <span v-else-if="showLoading" class="th-loading th-loading-bars th-loading-lg mx-auto my-2 block text-grey-450"></span>
                </template>
            </TimeLine>

            <div ref="anchor" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import _ from 'lodash';
import { computed, nextTick, onMounted, ref, watch, type ComponentPublicInstance } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import { PtIcon } from 'patsnap-biz'
import { useI18n } from 'vue-i18n'
import { useNavBarState } from '@eureka/shared/nav-bar/src/composables/use-navbar-store';

import type { GenerateLoadingProcess } from '@/types';
import iconCopilotWhite from '@/assets/icons/copilot-white.svg';
import iconCopilot from '@/assets/icons/copilot.svg';
import iconAdd from '@patsnap-ui/icon/assets/solid/AddMedium.svg';
import iconTrash from '@patsnap-ui/icon/assets/solid/delete.svg';
import CreateStepRow from './CreateStepRow.vue'
import TimeLine from '../time-line/TimeLine.vue'
import { BiTrackService } from '@eureka/domains/src/bi-track/services/bi-track-service';
import { EurekaTrackEventType } from '@eureka/domains/src/bi-track/types';
import { useStep } from '@/composables/use-step';
import { useRouter } from 'vue-router';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { PatsnapCopilotService } from '@analytics/patsnap-copilot/src/patsnap-copilot-service.interface';
import type { ProjectCopilotServiceImpl } from '@/services/project-copilot-service';

const anchor = ref<HTMLDivElement | null>(null)
const container = ref<ComponentPublicInstance>()

const copilotService = injectService<PatsnapCopilotService>(PatsnapCopilotService) as unknown as ProjectCopilotServiceImpl;
const biTrackService = injectService<BiTrackService>(BiTrackService);

const { displayQuestion, directory, create, currentStep, customizeDirectory, isAnalyzing, isProjectFinished } = useStep()
const MAX_CUSTOMIZE_DIRECTION = 2;
// 创建完成等待时间
const WAIT_SECONDS = 2;
// 创建完成倒计时
const countdown = ref(-1)

const props = defineProps<{
    createStepList: GenerateLoadingProcess[],
    isStreamProcessing: boolean,
    streamStarting: boolean,
}>()

const { t } = useI18n()
const router = useRouter()
const { queryList } = useNavBarState();

const customizeInputRef = ref<HTMLElement>();
const isSubmitting = ref(false);

// 记录初始的customizeDirectory状态，用于判断是否有修改
const initialCustomizeDirectory = ref<typeof customizeDirectory.value>([]);

const titleLabel = computed(() => {
    return props.isStreamProcessing ? t('chat.copilotUnderstand.title') : t('chat.copilotUnderstand.done')
})

const showLoading = computed(() => {
    return props.streamStarting && !props.isStreamProcessing
})

const showOutlineAdd = computed(() => {
    return customizeDirectory.value.length < MAX_CUSTOMIZE_DIRECTION && !isProjectFinished.value;
})

watch(() => showLoading.value, () => {
    if (currentStep.value <= 1) {
        currentStep.value++;
    }
})

const handleDirectoryRemove = (index: number) => {
    customizeDirectory.value = customizeDirectory.value.filter((dirItem, idx) => index !== idx)
}

const formData = computed(() => {
    const normalizedCustomizeDirectory = customizeDirectory.value.filter((item) => {
        return _.trim(item.name);
    });
    return [
        ...directory.value,
        ...normalizedCustomizeDirectory
    ];
})

const exploreDisabled = computed(() => {
    return isAnalyzing.value || currentStep.value !== 2 || isSubmitting.value || isProjectFinished.value;
})

const handleCreateClick = async() => {
    isSubmitting.value = true;

    // 判断用户是否对customizeDirectory进行了修改
    const hasCustomizeModification = !_.isEqual(initialCustomizeDirectory.value, customizeDirectory.value);
    if (hasCustomizeModification) {
        biTrackService.biTrack(EurekaTrackEventType.QUICK_RESEARCH, {
            sourceType: 'outline',
            otherParam: 'edit',
        });
    }

    create(formData.value).then((project) => {
        countdown.value = WAIT_SECONDS;
        return new Promise((resolve) => {
            const t = setInterval(() => {
                countdown.value -= 1;
                if (countdown.value <= 0) {
                    resolve(null);
                    clearInterval(t);
                }
            }, 1000)
        })
    }).then(() => {
        router.push({
            name: 'report',
            params: {
                projectId: copilotService.jobId,
            },
        });
        queryList();
    }).finally(() => {
        isSubmitting.value = false;
    });
}

function handleAddClick() {
    customizeDirectory.value = [
        ...customizeDirectory.value,
        {
            name: '',
            source_type: 'customize',
        }
    ];
    nextTick(() => {
        _.last(customizeInputRef.value as unknown as HTMLInputElement[])?.focus?.();
    })
    biTrackService.biTrack(EurekaTrackEventType.QUICK_RESEARCH, {
        sourceType: 'outline',
        otherParam: 'add_new',
    });
}

const steps = [
    t('chat.stepExplore.name'),
    t('chat.stepOutline.name'),
    t('chat.stepGenerate.name')
]
onMounted(() => {
    // 记录初始状态
    initialCustomizeDirectory.value = JSON.parse(JSON.stringify(customizeDirectory.value));

    useResizeObserver(container.value!.$el, (entries) => {
        anchor.value!.scrollIntoView({
            block: 'start',
        })
    })
})
</script>

<style lang="scss">
@keyframes jump {
    0%,
    100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-5px);
    }
}

.jump {
    animation: jump 1s ease-in-out infinite;
}
</style>
